<template>
  <Modal
    v-model="dialogVisible"
    class-name="short-review-preview-dialog"
    title="在线预览"
    width="1000"
    :styles="{ top: '40px' }"
    :mask-closable="false"
    @on-cancel="handleClose"
  >
    <div class="preview-dialog-body">
      <Spin v-if="pageLoading" fix size="large" />
      <!-- 与短剧管理 /short-video → series-play 在线预览一致：VideoPlayer + 393.75×700 -->
      <div class="preview-main">
        <div class="preview-player-column">
          <Spin v-if="previewLoading" fix />
          <VideoPlayer
            v-if="videoUrl"
            :key="videoKey"
            ref="videoPlayerRef"
            class="preview-series-player"
            :url="videoUrl"
            preview-subtitle-tabs
            :subtitles="previewSubtitles"
            v-model="currentSubtitle"
          />
          <div v-else-if="!previewLoading" class="preview-player-empty">
            <div class="preview-player-title preview-player-title--empty">
              {{ displayTitle }}
            </div>
            <div class="preview-player-empty-text">暂无预览文件</div>
            <div class="preview-player-episode preview-player-episode--empty">
              第 {{ currentEpisode }} 集 · 第 {{ currentEpisode }} 集
            </div>
          </div>
        </div>

        <div class="preview-episode-panel">
          <div v-if="!episodeList.length" class="preview-episode-empty">
            暂无集数数据
          </div>
          <div v-else class="preview-episode-scroll">
            <div
              v-for="ep in episodeList"
              :key="ep"
              class="preview-episode-cell"
            >
              <div
                class="preview-episode-btn"
                :class="{
                  'preview-episode-btn--active': currentEpisode === ep,
                  'preview-episode-btn--disabled': previewLoading && pendingEpisode === ep
                }"
                @click="selectEpisode(ep)"
              >
                <span class="preview-episode-num">{{ ep }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <template #footer>
      <Button @click="handleClose">关闭</Button>
    </template>
  </Modal>
</template>

<script>
import VideoPlayer from '@/components/VideoPlayer'
import { SUBTITLE_OFF } from '@/utils/previewSubtitle'
import {
  getTtMediaReviewMaterials,
  getTtMediaReviewPreview
} from '@/api/short-review'
function resolveTtVersionFromRow(row) {
  if (!row) return undefined
  const v = row.reviewTtVersion
  if (v != null && v !== '' && Number(v) > 0) {
    return Number(v)
  }
  return undefined
}

function buildEpisodeListFromMaterials(data) {
  const episodes = data && data.episodes
  if (Array.isArray(episodes) && episodes.length) {
    const nums = episodes
      .map((item) => Number(item.seriesNum))
      .filter((n) => !Number.isNaN(n) && n > 0)
    return [...new Set(nums)].sort((a, b) => a - b)
  }
  const album = (data && data.album) || {}
  const count = Number(album.totalEpisodes || album.episodeCount || 0)
  if (count > 0) {
    return Array.from({ length: count }, (_, i) => i + 1)
  }
  return []
}

export default {
  name: 'ShortReviewPreviewDialog',
  components: {
    VideoPlayer
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      pageLoading: false,
      previewLoading: false,
      pendingEpisode: null,
      currentEpisode: 1,
      episodeList: [],
      previewSubtitles: [],
      currentSubtitle: SUBTITLE_OFF,
      videoUrl: '',
      videoKey: 0,
      ttVersionQuery: undefined,
      _previewFetching: false,
      _previewFetchKey: ''
    }
  },
  computed: {
    displayTitle() {
      const row = this.row || {}
      return row.playName || '—'
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.$nextTick(() => {
          if (this.visible) {
            this.bootstrapPreview()
          }
        })
      } else {
        this.resetPreviewData()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
      if (!val) {
        this.resetPreviewData()
      }
    },
  },
  methods: {
    resetPreviewData() {
      this.pageLoading = false
      this.previewLoading = false
      this.pendingEpisode = null
      this.currentEpisode = 1
      this.episodeList = []
      this.videoUrl = ''
      this.videoKey += 1
      this.ttVersionQuery = undefined
      this._previewFetching = false
      this._previewFetchKey = ''
      this.previewSubtitles = []
      this.currentSubtitle = SUBTITLE_OFF
      this.pausePreviewVideo()
    },
    bootstrapPreview() {
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.$Message.error('缺少审核记录，无法预览')
        return
      }
      this.ttVersionQuery = resolveTtVersionFromRow(this.row)
      this.pageLoading = true
      const materialsParams = {}
      if (this.ttVersionQuery != null) {
        materialsParams.ttVersion = this.ttVersionQuery
      }
      getTtMediaReviewMaterials(id, materialsParams)
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.episodeList = buildEpisodeListFromMaterials(res.data)
          } else {
            this.episodeList = []
          }
          const firstEp = this.episodeList[0] || 1
          this.currentEpisode = firstEp
          return this.fetchPreview(firstEp, { silentFail: true })
        })
        .catch(() => {
          this.episodeList = []
          this.currentEpisode = 1
          return this.fetchPreview(1, { silentFail: true })
        })
        .finally(() => {
          this.pageLoading = false
        })
    },
    buildPreviewParams(seriesNum) {
      const params = {}
      if (seriesNum != null && seriesNum !== '') {
        params.seriesNum = Number(seriesNum)
      }
      if (this.ttVersionQuery != null) {
        params.ttVersion = this.ttVersionQuery
      }
      return params
    },
    fetchPreview(seriesNum, options) {
      const id = this.row && this.row.id
      if (id == null || id === '') return Promise.resolve()
      const params = this.buildPreviewParams(seriesNum)
      const fetchKey = `${id}-${params.seriesNum || ''}-${params.ttVersion || ''}`
      if (this._previewFetching && this._previewFetchKey === fetchKey) {
        return Promise.resolve()
      }
      this._previewFetching = true
      this._previewFetchKey = fetchKey
      this.previewLoading = true
      return getTtMediaReviewPreview(id, params)
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.applyPreviewData(res.data, seriesNum)
          } else {
            this.clearPreviewMedia()
            if (!options || !options.silentFail) {
              this.$Message.error(res.message || '获取预览失败')
            }
          }
        })
        .catch(() => {
          this.clearPreviewMedia()
          if (!options || !options.silentFail) {
            this.$Message.error('获取预览失败')
          }
        })
        .finally(() => {
          this.previewLoading = false
          this.pendingEpisode = null
          this._previewFetching = false
        })
    },
    applyPreviewData(data, requestedSeriesNum) {
      const num = Number(data.seriesNum)
      if (!Number.isNaN(num) && num > 0) {
        this.currentEpisode = num
      } else if (requestedSeriesNum != null) {
        this.currentEpisode = Number(requestedSeriesNum) || 1
      }
      if (!this.episodeList.length && this.currentEpisode) {
        this.episodeList = [this.currentEpisode]
      } else if (
        this.episodeList.length &&
        !this.episodeList.includes(this.currentEpisode)
      ) {
        this.episodeList = [...this.episodeList, this.currentEpisode].sort(
          (a, b) => a - b
        )
      }
      this.videoUrl = (data.videoUrl && String(data.videoUrl).trim()) || ''
      this.previewSubtitles = Array.isArray(data.subtitles)
        ? data.subtitles
        : []
      this.videoKey += 1
    },
    clearPreviewMedia() {
      this.videoUrl = ''
      this.previewSubtitles = []
      this.currentSubtitle = SUBTITLE_OFF
    },
    pausePreviewVideo() {
      const wrapper = this.$refs.videoPlayerRef
      if (wrapper && typeof wrapper.pause === 'function') {
        wrapper.pause()
      }
    },
    selectEpisode(ep) {
      if (this.previewLoading || ep === this.currentEpisode) return
      this.pausePreviewVideo()
      this.pendingEpisode = ep
      this.currentEpisode = ep
      this.fetchPreview(ep)
    },
    handleClose() {
      this.pausePreviewVideo()
      this.dialogVisible = false
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.preview-dialog-body {
  position: relative;
}

.preview-main {
  display: flex;
  align-items: flex-start;
  min-height: 700px;
}

.preview-player-column {
  position: relative;
  flex-shrink: 0;
  width: 393.75px;
  margin-right: 20px;
  overflow: visible;
}

/* 字幕 Tab 在 VideoPlayer 内、视频正下方，勿撑满整列高度 */
.preview-series-player {
  width: 100%;
  height: auto;
}

.preview-series-player >>> .video-player-root--preview-tabs .video-player-wrapper {
  height: 700px;
}

.preview-series-player >>> .video-player-wrapper .video-js {
  width: 100%;
  height: 100%;
}

.preview-player-empty {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 700px;
  padding: 24px 16px 20px;
  box-sizing: border-box;
  color: #fff;
  text-align: center;
  background: linear-gradient(180deg, #1a2744 0%, #2f4a7a 48%, #1e3358 100%);
}

.preview-player-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.preview-player-title--empty {
  position: absolute;
  top: 20px;
  left: 16px;
  right: 16px;
}

.preview-player-empty-text {
  font-size: 14px;
  line-height: 22px;
  opacity: 0.85;
}

.preview-player-episode {
  font-size: 13px;
  line-height: 20px;
  opacity: 0.9;
}

.preview-player-episode--empty {
  position: absolute;
  bottom: 18px;
  left: 16px;
  right: 16px;
}

.preview-episode-panel {
  flex: 1;
  min-width: 0;
  height: 700px;
  min-height: 700px;
  overflow: hidden;
}

.preview-episode-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 14px;
  color: #909399;
}

.preview-episode-scroll {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  margin: 5px -5px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.preview-episode-cell {
  width: calc(100% / 11);
  padding: 5px;
  box-sizing: border-box;
  line-height: normal;
}

.preview-episode-btn {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  border: 1px solid #aaa;
  border-radius: 3px;
  cursor: pointer;
  font-size: 15px;
  color: #303133;
  background: #fff;
  box-sizing: border-box;
}

.preview-episode-btn--active {
  background-color: #2d98f1;
  color: #fff;
  border-color: #2d98f1;
}

.preview-episode-btn--disabled {
  opacity: 0.6;
  cursor: wait;
}

.preview-episode-num {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}
</style>

<style>
.short-review-preview-dialog .ivu-modal {
  width: 1000px !important;
}

.short-review-preview-dialog .ivu-modal-content {
  width: 1000px !important;
}

.short-review-preview-dialog .ivu-modal-header {
  height: 76px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8eaec;
}

.short-review-preview-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  color: #263f58;
  line-height: 24px;
}

.short-review-preview-dialog .ivu-modal-close {
  top: 22px;
  right: 20px;
  z-index: 1;
}

.short-review-preview-dialog .ivu-modal-body {
  padding: 20px 24px 16px;
}

.short-review-preview-dialog .ivu-modal-footer {
  height: 66px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #e8eaec;
}

</style>
